import {
  BroadcastChannel,
  createLeaderElection,
  type LeaderElector,
} from "broadcast-channel";

export interface TabLeadershipState {
  isLeader: boolean;
  isElecting: boolean;
  error: string | null;
}

type StateUpdateCallback = (state: TabLeadershipState) => void;

// Long enough for the polling LeaderElection fallback (browsers without
// navigator.locks) to win an uncontested election, which takes ~1s.
export const ELECTION_TIMEOUT_MS = 5000;

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export class TabLeadershipManager {
  private channel: BroadcastChannel | null = null;
  private election: LeaderElector | null = null;
  private initialized = false;
  private state: TabLeadershipState = {
    isLeader: false,
    isElecting: true,
    error: null,
  };
  private callbacks = new Set<StateUpdateCallback>();

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    try {
      this.updateState({ isElecting: true, error: null, isLeader: false });

      this.channel = new BroadcastChannel("elephant-primary-care-tabs");
      this.election = createLeaderElection(this.channel);

      window.addEventListener("beforeunload", this.handleWindowUnload);

      const wonLeadership = this.election
        .awaitLeadership()
        .then(() => {
          this.updateState({ isLeader: true, isElecting: false, error: null });
          return true;
        })
        .catch((err: Error) => {
          this.updateState({
            isLeader: false,
            isElecting: false,
            error: err.message,
          });
          return false;
        });

      await Promise.race([wonLeadership, wait(ELECTION_TIMEOUT_MS)]);

      if (this.state.isElecting) {
        this.updateState({ isLeader: false, isElecting: false, error: null });
      }
    } catch (err) {
      this.updateState({
        error: (err as Error).message,
        isElecting: false,
        isLeader: false,
      });
    }
  }

  async cleanup(): Promise<void> {
    window.removeEventListener("beforeunload", this.handleWindowUnload);
    if (this.election) {
      await this.election.die();
      this.election = null;
    }
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.initialized = false;
    this.callbacks.clear();
  }

  subscribe(callback: StateUpdateCallback): () => void {
    this.callbacks.add(callback);
    callback(this.state);

    return () => {
      this.callbacks.delete(callback);
    };
  }

  getCurrentState(): TabLeadershipState {
    return this.state;
  }

  private updateState(newState: Partial<TabLeadershipState>): void {
    this.state = { ...this.state, ...newState };
    for (const callback of this.callbacks) {
      callback(this.state);
    }
  }

  private handleWindowUnload = (): void => {
    if (this.election) {
      this.election.die();
    }
    if (this.channel) {
      this.channel.close();
    }
  };
}

export const tabLeadershipManager = new TabLeadershipManager();
